import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CmsContent from '@components/CmsCard/CmsCard';
import AlertModal from '@components/AlertModal/AlertModal';
import { PaperlessEnrollmentProps } from '@components/types';
import { selectHasPaperless, selectHasRefreshed } from '@store/selectors/widgets.selectors';
import { ModalAlert } from '@components/ModalAlert/ModalAlert';
import { AmplifyLogs, cmsNameToID, UIConstants } from '@constants';
import { selectContent } from '@store/selectors/cmsContent.selector';
import { fetchPaperlessAsync } from '@store/actions/paperless.action';
import { selectPaperlessState } from '@store/selectors/paperless.selector';
import { NewCardWrapper } from '@components/NewCard/NewCard.styled';
import { fetchWidgetRefreshAsync } from '@store/actions/widgets.actions';
import Loader from '@components/Loader/Loader';
import { logUserEvent } from '@analytics/amplifyLogging';

const PaperlessEnrollment: React.FC<PaperlessEnrollmentProps> = ({ selectedAccount, themeType }) => {
    const dispatch = useDispatch();

    const paperlessContent: string = useSelector(selectContent(cmsNameToID.paperless));
    const hasPaperless = useSelector(selectHasPaperless);
    const paperlessData = useSelector(selectPaperlessState);
    const isEligibleForPaperless = hasPaperless?.initParam?.find(({ value }) => value === selectedAccount?.accountNumber);
    const hasRefreshed = useSelector(selectHasRefreshed);

    const {
        confirmationTitle,
        confirmationDetails,
        confirmationHeading,
        enrollNow,
        checkIconGreen,
        close,
        submittedDetails,
        errorTitle,
        ok,
        enrollErrorDetails,
        closeIconRed
    } = UIConstants;
    const [enroll, setEnroll] = useState(false);
    const [enrollSuccess, setEnrollSuccess] = useState(false);
    const [enrollError, setEnrollError] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleEnrollNow = () => {
        const attributes = {
            ...AmplifyLogs.paperlessEnrolment
        };
        logUserEvent(attributes);
        setEnroll(!enroll);
    };
    const handleButtonClick = (type: string) => {
        console.log(type);
        if (type.toUpperCase() === close) {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
            dispatch(fetchWidgetRefreshAsync.request('?refresh=true'));
            setShowLoader(true);
        } else if (type === enrollNow) {
            setShowLoader(true);
            dispatch(fetchPaperlessAsync.request(selectedAccount?.accountNumber));
            setEnroll(false);
        } else if (type === ok) {
            setEnrollError(false);
        } else if (type === 'close_modal' && enrollSuccess) {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
            dispatch(fetchWidgetRefreshAsync.request('?refresh=true'));
            setShowLoader(true);
        } else if (type === 'closeEnroll' || type === 'close_modal') {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
        }
    };

    useEffect(() => {
        if (paperlessContent) {
            document.getElementById('enrollPaperless')?.addEventListener('click', handleEnrollNow);
        }
        if (hasRefreshed) {
            setShowLoader(false);
        }
    }, [paperlessContent, hasRefreshed, handleEnrollNow]);
    useEffect(() => {
        if (paperlessData?.success) {
            setShowLoader(false);
            setEnrollSuccess(true);
        }
        if (paperlessData?.hasError) {
            setShowLoader(false);
            setEnrollError(true);
        }
    }, [paperlessData]);

    return (
        <>
            <Loader showLoader={showLoader} />

            {isEligibleForPaperless && (
                <NewCardWrapper themeType={themeType}>
                    <CmsContent
                        data-testId="enrollNowButton"
                        contentId="14f38897-00eb-4834-82a7-6ec95ddec7d9"
                        handleEnrollNow={handleEnrollNow}
                    />
                </NewCardWrapper>
            )}

            {/* Modal needs to be updated with the appropriate content */}
            {(enroll || enrollSuccess) && (
                <AlertModal
                    maxWidth={enroll ? '54rem' : '25rem'}
                    title={enroll ? `${confirmationHeading}  ${selectedAccount?.accountNumber}` : confirmationTitle}
                    handleClose={ele => handleButtonClick(ele)}
                >
                    <ModalAlert
                        data-testid="enrollSuccessLink"
                        details={enroll ? confirmationDetails : submittedDetails}
                        buttonText={enroll ? enrollNow : close}
                        image={enroll ? null : checkIconGreen}
                        handleClose={ele => handleButtonClick(ele)}
                    />
                </AlertModal>
            )}
            {enrollError && (
                <AlertModal title={errorTitle} handleClose={ele => handleButtonClick(ele)}>
                    <ModalAlert
                        data-testid="enrollErrorButton"
                        details={enrollErrorDetails}
                        buttonText={ok}
                        image={closeIconRed}
                        handleClose={ele => handleButtonClick(ele)}
                    />
                </AlertModal>
            )}
        </>
    );
};

export default React.memo(PaperlessEnrollment);
